import { RotateCcw } from 'lucide-react';
import { createContext, useCallback, useContext, useState } from 'react';

import {
    PageTour,
    type PageTourStep,
} from '@apps/web/components/page_tour/page_tour';
import { Button } from '@apps/web/components/ui/button';

type PageTourReplayContextValue = {
    readonly replay: () => void;
    readonly showReplay: boolean;
};

const PageTourReplayContext = createContext<PageTourReplayContextValue | null>(
    null,
);

/**
 * Wraps a page that has a guided tour. Renders the tour popover and exposes
 * replay through `PageTourReplayButton` anywhere inside the tree.
 */
export function PageTourScope({
    children,
    enabled = true,
    showReplayButton = false,
    ready,
    userId,
    seenStorageKey,
    steps,
    testIdPrefix = 'page-tour',
    onReplay,
}: {
    readonly children: React.ReactNode;
    readonly enabled?: boolean;
    readonly showReplayButton?: boolean;
    readonly ready: boolean;
    readonly userId: string | null;
    readonly seenStorageKey: string | null;
    readonly steps: ReadonlyArray<PageTourStep>;
    readonly testIdPrefix?: string;
    readonly onReplay?: () => void;
}) {
    const [replayToken, setReplayToken] = useState(0);
    const replay = useCallback(() => {
        onReplay?.();
        setReplayToken((token) => token + 1);
    }, [onReplay]);

    if (!enabled) {
        return children;
    }

    return (
        <PageTourReplayContext.Provider
            value={{ replay, showReplay: showReplayButton }}
        >
            {children}
            <PageTour
                ready={ready}
                userId={userId}
                seenStorageKey={seenStorageKey}
                steps={steps}
                testIdPrefix={testIdPrefix}
                replayToken={replayToken}
            />
        </PageTourReplayContext.Provider>
    );
}

export function PageTourReplayButton() {
    const context = useContext(PageTourReplayContext);
    if (context === null || !context.showReplay) {
        return null;
    }

    return (
        <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground h-7 gap-1.5 px-2 text-xs"
            data-testid="page-tour-replay"
            onClick={context.replay}
        >
            <RotateCcw className="size-3.5" />
            Replay tour
        </Button>
    );
}
